import { useMemo, useRef, useState } from 'react';
import { Link, NavigateFunction, useNavigate } from 'react-router-dom';
import {
    Row, Col, Tab, Card,
    Button,
    Table,
} from 'react-bootstrap';
import Nav from 'react-bootstrap/Nav';
import {
    InstrumentArtefactDto,
    DashboardItemDto, DashboardQuoteDto, ReportDto,
    RequestForQuoteDto,
} from '../../api/web-api-client';
import Actions, { DropdownActionItem } from '../Actions';
import useAccountContext from '../../authentication/hooks';
import { DashboardItemStatus, ReportStatus } from '../../routes/common/enums';
import { validQuoteIdStatus, viewArtefactHeadingStatus } from '../../routes/common/quoteStatus';
import ContactDetails from '../Utilities/contactDetails';
import { trackGAEvent } from '../../analytics/GoogleAnalytics';

// TS Move this to a common location
const formattedDate = (dateToFormat : Date | string | undefined) => (dateToFormat ? new Date(dateToFormat).toLocaleDateString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
}) : '');

const DateColumn = (props: { date: Date | string | undefined }) => {
    const { date } = props;
    return <Col className='small'>{formattedDate(date)}</Col>;
};

const renderRequestContent = (requestForQuote: RequestForQuoteDto, sourceReferenceId: string | undefined) => {
    const {
        serialNumber,
        manufacturer,
        model,
        description,
        measurementCategory,
        artefactType,
        requestSubmitted,
        contactDetails,
    } = requestForQuote;

    // Request tab content placeholder (1 col pairs)
    return (
        <Row>
            <Col lg={4} md={6}>
                {/*  Column 1 */}
                <Row>
                    <Col>
                        <h4 className='h6 mb-0'>Instrument/artefact</h4>
                    </Col>
                </Row>
                {!!(serialNumber) && (
                    <Row>
                        <Col className='fw-bold small'>Serial:</Col>
                        <Col className='small'>{serialNumber}</Col>
                    </Row>
                )}
                <Row>
                    <Col className='fw-bold small'>Manufacturer:</Col>
                    <Col className='small'>{manufacturer}</Col>
                </Row>
                <Row>
                    <Col className='fw-bold small'>Model:</Col>
                    <Col className='small'>{model}</Col>
                </Row>
                {!!(description) && (
                    <Row>
                        <Col className='fw-bold small'>Description:</Col>
                        <Col className='small'>{description}</Col>
                    </Row>
                )}
            </Col>
            <Col lg={4} md={6}>
                {/*  Column 2 */}
                <Row>
                    <Col>
                        <h4 className='h6 mb-0'>
                            <span className='visually-hidden'>Measurement type</span>
                            <br />
                        </h4>
                    </Col>
                </Row>
                <Row>
                    <Col className='fw-bold small'>Measurement category:</Col>
                    <Col className='small'>{measurementCategory}</Col>
                </Row>
                <Row>
                    <Col className='fw-bold small'>Instrument/artefact type:</Col>
                    <Col className='small'>{artefactType}</Col>
                </Row>
                {!!(requestSubmitted) && (
                    <Row>
                        <Col className='fw-bold small'>Request submitted:</Col>
                        <DateColumn date={requestSubmitted} />
                    </Row>
                )}
            </Col>
            <Col lg={4} md={6}>
                {/*  Column 3 */}
                {!!(sourceReferenceId) && (
                    <Row>
                        <Col xs={6} md={5} lg={5} className='fw-bold small'>
                            <span className='text-nowrap'>
                                <i
                                    className='icon-info bgCircle me-1'
                                    aria-label='Info note: This is the previous request Ref ID'
                                    title='Info note: This is the previous request Ref ID'
                                />
                                Previous
                            </span>
                            {' '}
                            <span className='text-nowrap'>Ref ID:</span>
                        </Col>
                        <Col xs={6} md={7} lg={7} className='small text-nowrap'>
                            {sourceReferenceId}
                        </Col>
                    </Row>
                )}
                <ContactDetails
                    label='Request main contact'
                    name={`${contactDetails?.firstName} ${contactDetails?.lastName}`}
                    businessPhone={contactDetails?.businessPhone}
                    mobilePhone={contactDetails?.mobilePhone}
                    email={contactDetails?.email}
                />
            </Col>
        </Row>
    );
};

// eslint-disable-next-line max-len
const renderQuotationContent = (dashboardQuoteDto: DashboardQuoteDto, quoteStatus: string | undefined, referenceId: string | undefined, navigate: NavigateFunction) => {
    // Quotation tab content placeholder (3 col pairs)
    const nmiOfficer = dashboardQuoteDto.nmiContactDetails;
    const useQuoteId = validQuoteIdStatus.some((x) => x === quoteStatus);
    return (
        <>
            <Row className='mb-3'>
                <Col md={4}>
                    {/*  Column 1 */}
                    <Row>
                        <Col>
                            <h4 className='h6 mb-0'>Instrument/artefact delivery</h4>
                        </Col>
                    </Row>
                    <Row className='mb-2'>
                        <Col className='fw-bold small'>Date required:</Col>
                        <DateColumn date={dashboardQuoteDto.dateRequired} />
                    </Row>
                </Col>
                <Col md={4}>
                    {/*  Column 2 */}
                    <Row>
                        <Col>
                            <h4 className='h6 mb-0 visually-hidden'>Quotation</h4>
                        </Col>
                    </Row>
                    <Row>
                        <Col className='fw-bold small'>Quotation ID:</Col>
                        <Col className='small'>{dashboardQuoteDto.quotationId}</Col>
                    </Row>
                    <Row>
                        <Col className='fw-bold small'>Offer date:</Col>
                        <DateColumn date={dashboardQuoteDto.offerDate} />
                    </Row>
                    <Row className='mb-2'>
                        <Col className='fw-bold small'>Valid until:</Col>
                        <DateColumn date={dashboardQuoteDto.validUntil} />
                    </Row>
                </Col>
                <Col md={4}>
                    {/*  Column 3 */}
                    <ContactDetails
                        label='NMI test officer'
                        name={`${nmiOfficer?.firstName} ${nmiOfficer?.lastName}`}
                        businessPhone={nmiOfficer?.businessPhone}
                        mobilePhone={nmiOfficer?.mobilePhone}
                        email={nmiOfficer?.email}
                    />
                    {/* <Row>
                        <Col>
                            <h4 className='h6 mb-0 visually-hidden'>Request</h4>
                        </Col>
                    </Row>
                    <Row className='mb-2'>
                        <Col className='fw-bold small'>Request submitted:</Col>
                        <DateColumn date={dashboardQuoteDto.requestSubmitted} />
                    </Row> */}
                </Col>
            </Row>
            <Row>
                <Col md className='text-center'>
                    <Button
                        variant='secondary'
                        data-testid={useQuoteId
                            ? `RefId-${dashboardQuoteDto.quotationId}-view-quotation-button`
                            : `RefId-${referenceId}-view-quotation-button`}
                        className='ms-md-auto'
                        onClick={() => (useQuoteId
                            ? navigate(`/quotation/${dashboardQuoteDto.quotationId}`)
                            : navigate(`/quotation/${referenceId}`))}
                    >
                        View quotation
                    </Button>
                </Col>
            </Row>
        </>
    );
};

// eslint-disable-next-line max-len
const renderInstrumentReportsDtoContent = (reportDto: ReportDto, requestForQuoteId: string | undefined, navigate: NavigateFunction, status: string | undefined, referenceId: string | undefined) => (
    // ReportDto tab content placeholder (3 col pairs)
    <>
        <Row>
            <Col md={4}>
                {/*  Column 1 */}
                <Row className='mb-2'>
                    <Col className='fw-bold small'>Invoice number:</Col>
                    <Col className='small'>{reportDto.invoiceNumber}</Col>
                </Row>
            </Col>
            <Col md={4} />
            <Col md={4} />
        </Row>
        <Row className='mb-3'>
            <Col md={4}>
                {/*  Column 1 */}
                <Row>
                    <Col>
                        <h4 className='h6 mb-0'>Instrument/artefact delivery</h4>
                    </Col>
                </Row>
                <Row>
                    <Col className='fw-bold small'>Date required:</Col>
                    <DateColumn date={reportDto.dateRequired} />
                </Row>
                <Row className='mb-2'>
                    <Col className='fw-bold small'>Date received:</Col>
                    <DateColumn date={reportDto.dateReceived} />
                </Row>
            </Col>
            <Col md={4}>
                {/*  Column 2 */}
                <Row>
                    <Col>
                        <h4 className='h6 mb-0'>Measurement report</h4>
                    </Col>
                </Row>
                <Row>
                    <Col className='fw-bold small'>Report ID:</Col>
                    <Col className='small'>{reportDto.reportId}</Col>
                </Row>
                <Row>
                    <Col className='fw-bold small'>Report date:</Col>
                    <DateColumn date={reportDto.dateIssued} />
                </Row>
                <Row className='mb-2'>
                    <Col className='fw-bold small'>Target report date:</Col>
                    <DateColumn date={reportDto.targetReportDate} />
                </Row>
            </Col>
            <Col md={4}>
                {/*  Column 3 */}
                <Row>
                    <Col>
                        <h4 className='h6 mb-0'>Instrument/artefact return</h4>
                    </Col>
                </Row>
                <Row>
                    <Col className='fw-bold small'>Return method:</Col>
                    <Col className='small'>{reportDto.returnMethod}</Col>
                </Row>
                <Row>
                    <Col className='fw-bold small'>Date dispatched:</Col>
                    <DateColumn date={reportDto.dateDispatched} />
                </Row>
                <Row>
                    <Col className='fw-bold small'>Carrier:</Col>
                    <Col className='small'>{reportDto.carrier}</Col>
                </Row>
                <Row className='mb-2'>
                    <Col className='fw-bold small'>Consignment note:</Col>
                    <Col className='small text-truncate'>
                        {reportDto.consignmentNote}
                    </Col>
                </Row>
            </Col>
        </Row>
        {status === DashboardItemStatus.ReportIssued
            && (
                <Row>
                    <Col />
                    <Col md className='text-center'>
                        <Button
                            variant='secondary'
                            data-testid={`RefId-${reportDto.reportId}-view-ReportDto-button`}
                            className='ms-md-auto mb-3'
                            onClick={() => navigate(`/report/${requestForQuoteId}`)}
                        >
                            View report
                        </Button>
                    </Col>
                    <Col md className='text-center'>
                        <Link
                            data-testid='request-for-quote-copy-button'
                            role='button'
                            to={`/request-for-quote-copy/${referenceId}`}
                            className='d-inline-block py-2 text-nowrap'
                        >
                            Request recalibration
                        </Link>
                    </Col>
                </Row>
            )}
    </>
);

const renderInstrumentReportsContent = (
    requestForQuoteId: string | undefined,
    artefact: InstrumentArtefactDto,
    navigate: NavigateFunction,
    referenceId: string | undefined,
) => (
    <>
        <Table
            id='instReports-table'
            data-testid='instReports-table'
            striped
            className='table-sm table-striped table-responsive-stack small mb-4'
            aria-live='off'
        >
            <caption id='caption-org-select-list' className='visually-hidden'>
                Measurement reports history for this instrument/artefact
            </caption>
            <thead key='instReportsHeadKeyForThead' className='visually-hidden'>
                <tr className='table-active'>
                    <th scope='col' className='col-md-2 text-nowrap'>Report date</th>
                    <th scope='col' className='col-md-2 text-nowrap'>Report ID</th>
                    <th scope='col' className='col-md-4 text-nowrap'>Measurement report</th>
                    <th scope='col' className='col-md-2 text-nowrap'>Measurement category</th>
                    {/* <th scope='col' className='col-md-2 text-nowrap'>Report Status</th> */}
                    <th scope='col' className='col-md-2 text-nowrap'><span className='visually-hidden'>Actions</span></th>
                </tr>
            </thead>
            <tbody key='instReportsTBodyKey'>
                <tr className='border-top-0'>
                    <td data-header='Report date' className='col-md-2 pe-0'>{formattedDate(artefact.tmasTcReportDate) || 'N/A'}</td>
                    <td data-header='Report ID' className='col-md-2'>
                        {artefact.tmasTcReportName}
                    </td>
                    <td data-header='Measurement report' className='col-md-4'>
                        {artefact.tmasMeasurementReportCertificateRequired}
                    </td>
                    <td data-header='Measurement category' className='col-md-2'>
                        {artefact.tmasMeasurementCategoryName}
                    </td>
                    {/* <td data-header='Report Status' className='col-md-2'>
                        <StatusPill status={artefact.tmasStatus!} />
                    </td> */}
                    <td data-header='Action' className='col-md-2'>
                        <div>
                            {artefact.tmasStatus !== ReportStatus.Withdrawn && (
                                <Link
                                    to={`/report/${requestForQuoteId}`}
                                    aria-label='View latest report'
                                    className='d-block mb-2'
                                >
                                    View report
                                </Link>
                            )}
                            <span className='visually-hidden'>
                                {' or '}
                            </span>

                            <Link
                                to={`/instrument-reports/${encodeURIComponent(artefact.tmasArtefactName!)}`}
                                aria-label='View all reports for this instrument/artefact'
                                className='d-block mb-2'
                            >
                                View all reports
                            </Link>
                        </div>
                    </td>
                </tr>
            </tbody>
        </Table>
        <Row>
            <Col />
            <Col md className='text-center'>
                {artefact.tmasStatus !== ReportStatus.Withdrawn && (
                    <Button
                        variant='secondary'
                        data-testid={`RefId-${artefact.tmasTcReportName}-view-latest-ReportDto-button`}
                        className='ms-md-auto mb-3'
                        onClick={() => navigate(`/report/${requestForQuoteId}`)}
                    >
                        View latest report
                    </Button>
                )}

            </Col>
            <Col md>
                <Link
                    data-testid='request-for-quote-copy-button'
                    role='button'
                    to={`/request-for-quote-copy/${referenceId}`}
                    className='d-inline-block py-2 text-nowrap'
                >
                    Request recalibration
                </Link>
            </Col>
        </Row>
    </>
);

const InstrumentItem = (props: { request: DashboardItemDto }) => {
    const {
        request,
    } = props;

    const {
        referenceId, status, requestedFor, lastUpdated, quote, report, requestForQuote, sourceReferenceId, artefact,
    } = request;

    const showArtefactHeading = useMemo(() => viewArtefactHeadingStatus.some((x) => x === status), [status]);
    const headingNoArtefact = requestForQuote?.manufacturer
        ? `${requestForQuote?.manufacturer} ${requestForQuote?.model || ''}` // If Model null display empty string
        : 'Draft Request For Quote';
    const heading = showArtefactHeading ? artefact?.tmasArtefactName : headingNoArtefact;
    const requestForQuoteId = request.quote?.quotationId;
    const [deleteId, setDeleteId] = useState<number>(); // TODO: Change dto to int
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const accountContext = useAccountContext();

    const cardRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    const onDelete = () => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        setDeleteDialogOpen(true);
        setDeleteId(requestForQuote?.id);
    };

    const navigate = useNavigate();

    const getActions = (): DropdownActionItem[] => {
        const actions: DropdownActionItem[] = [];

        // TODO: Move this to an enum? refactor this...
        switch (status) {
            case DashboardItemStatus.QuoteDrafted:
                actions.push({
                    action: 'Edit',
                    text: 'Edit request',
                    route: `/request-for-quote/${referenceId}`,
                    onClick: () => trackGAEvent('Edit request'),
                });
                break;
            case DashboardItemStatus.ReportIssued:
                actions.push({
                    action: 'View',
                    text: 'View latest report',
                    route: `/report/${requestForQuoteId}`,
                    onClick: () => trackGAEvent('View latest report'),
                });
                actions.push({
                    action: 'View',
                    text: 'Request recalibration',
                    route: `/request-for-quote-copy/${referenceId}`,
                    onClick: () => trackGAEvent('Request recalibration'),
                });
                break;
            case DashboardItemStatus.ReportWithdrawn:
                actions.push({
                    action: 'View',
                    text: 'Request recalibration',
                    route: `/request-for-quote-copy/${referenceId}`,
                    onClick: () => trackGAEvent('Request recalibration'),
                });
                break;
            case DashboardItemStatus.ReportInProgress:
                actions.push({
                    action: 'View',
                    text: 'Request recalibration',
                    route: `/request-for-quote-copy/${referenceId}`,
                    onClick: () => trackGAEvent('Request recalibration'),
                });
                break;
            case DashboardItemStatus.QuoteAvailable:
                actions.push({
                    action: 'View',
                    text: 'View/accept quotation',
                    route: `/quotation/${referenceId}`,
                    onClick: () => trackGAEvent('View/accept quotation'),
                });
                break;
            default:
                break;
        }

        return actions;
    };

    // Card aria-labelledby
    const cardSummaryId = `card-instr-summary-${referenceId}`;
    const cardDetailsId = `card-instr-details-${referenceId}`;
    const cardTabContentId = `card-instr-tab-content-${referenceId}`;

    const labelledBy = isFocused
        ? '' // `${cardSummaryId} ${cardDetailsId} ${cardTabContentId}`
        : cardSummaryId;

    return (
        <li>
            <Card
                ref={cardRef}
                id={`RefId-${referenceId}`}
                key={`RefId-${referenceId}`}
                className='mb-4 p-2 shadow'
                tabIndex={0}
                aria-labelledby={labelledBy}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
            >
                <Card.Body>
                    <Row>
                        <Col lg={8}>
                            <h3 id={cardSummaryId} className='mb-1'>
                                {heading ? `${heading}` : 'Draft request for Quote'}
                            </h3>
                            <p>
                                <span className='d-block small mb-2'>
                                    Testing and calibration service
                                </span>
                            </p>
                        </Col>
                        <Col lg={3} className='py-1' aria-hidden={!isFocused}>
                            <Row>
                                <Col className='fw-bold small text-nowrap'>Most recent</Col>
                                <Col className='small text-nowrap' />
                            </Row>
                            <Row>
                                <Col className='fw-bold small text-nowrap'>Report ID:</Col>
                                <Col className='small text-nowrap'>
                                    {artefact?.tmasTcReportName}
                                </Col>
                            </Row>
                            <Row>
                                <Col className='fw-bold small'>Report date:</Col>
                                <Col className='small'>
                                    {formattedDate(artefact?.tmasTcReportDate)}
                                </Col>
                            </Row>
                            <Row>
                                <Col className='fw-bold small'>Requested for:</Col>
                                <Col className='small'>
                                    {requestedFor}
                                </Col>
                            </Row>
                        </Col>
                        <Col lg={1} className='text-end'>
                            <Actions
                                id={`actions-${referenceId}`}
                                as='icon'
                                variant='Blue'
                                dropDownActions={getActions()}
                                align='start'
                                containerClassName='dash-item-actions align-self-center'
                                onItemClick={onDelete()}
                                // buttonAriaTitle={
                                //     ` menu for ${heading ? `${heading}` : 'Draft request for Quote'},
                                // Ref ID: ${referenceId}`
                                // }
                            />
                        </Col>
                    </Row>
                    <Row
                        id={cardDetailsId}
                        aria-hidden={!isFocused}
                    >
                        <Col>
                            <Tab.Container
                                id={`tabs-${referenceId}`}
                                defaultActiveKey='reports'
                            >
                                <Nav
                                    as='ul'
                                    variant='underline'
                                    className='flex-nowrap overflow-x-auto no-scrollbars'
                                >
                                    <Nav.Item as='li' role='presentation'>
                                        <Nav.Link eventKey='details' className='px-3'>Details</Nav.Link>
                                    </Nav.Item>

                                    <Nav.Item as='li' role='presentation'>
                                        <Nav.Link eventKey='reports' className='px-3'>Reports</Nav.Link>
                                    </Nav.Item>

                                </Nav>
                                <Tab.Content
                                    id={cardTabContentId}
                                    aria-hidden={!isFocused}
                                >
                                    <Tab.Pane eventKey='details' tabIndex={0}>
                                        {requestForQuote && renderRequestContent(requestForQuote, sourceReferenceId)}
                                    </Tab.Pane>
                                    <Tab.Pane eventKey='reports' tabIndex={0}>
                                        {artefact && renderInstrumentReportsContent(requestForQuoteId, artefact, navigate, referenceId)}
                                    </Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </li>
    );
};

export default InstrumentItem;
